import React, { useState } from 'react';
import { Container, Box, Typography, TextField, Button, Alert, Paper, Link as MuiLink } from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';
import { loginUser, sendForgotPasswordOtp, resetPassword, otpErrorMessage } from '../api/apiClient';
import { setToken } from '../api/session';
import { setWorkflowOwner } from '../mock/localWorkflowStore';
import { AUTOFILL_SX } from '../theme/formStyles';
import PasswordVisibilityToggle from '../components/PasswordVisibilityToggle';

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [view, setView] = useState('login'); // 'login', 'forgot_email', 'forgot_otp'
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotOtp, setForgotOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const data = await loginUser(email, password);
      setToken(data.access_token);
      // Scope local workflow data to this user; wipe it if a different
      // account was previously used on this browser.
      setWorkflowOwner(email.trim().toLowerCase());
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  const handleSendForgotOtp = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      setError('Please enter your email.');
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await sendForgotPasswordOtp(forgotEmail);
      setView('forgot_otp');
      setSuccess('OTP sent to your email.');
    } catch (err) {
      setError(otpErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await resetPassword(forgotEmail, forgotOtp, newPassword);
      setView('login');
      setSuccess('Password reset successfully. You can now login.');
      setForgotEmail('');
      setForgotOtp('');
      setNewPassword('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xs">
      <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ p: 4, width: '100%', borderRadius: 2 }}>
          <Typography variant="h5" align="center" gutterBottom fontWeight="bold">
            {view === 'login' ? 'Sign In to OnePass' : 'Reset Password'}
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          {view === 'login' && (
            <Box component="form" onSubmit={handleLogin} sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                placeholder="you@example.com"
                sx={AUTOFILL_SX}
                InputLabelProps={{ shrink: true }}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                placeholder="Enter your password"
                sx={AUTOFILL_SX}
                InputLabelProps={{ shrink: true }}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <PasswordVisibilityToggle
                      visible={showPassword}
                      onToggle={() => setShowPassword((prev) => !prev)}
                    />
                  ),
                }}
              />
              <Box textAlign="right" sx={{ mt: 1 }}>
                <MuiLink component="button" type="button" variant="body2" onClick={() => { setView('forgot_email'); setError(null); setSuccess(null); }}>
                  Forgot Password?
                </MuiLink>
              </Box>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2, py: 1.5 }}
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
              <Box textAlign="center">
                <Typography variant="body2">
                  Don't have an account?{' '}
                  <MuiLink component={Link} to="/build/register" variant="body2">
                    Sign Up
                  </MuiLink>
                </Typography>
              </Box>
            </Box>
          )}

          {view === 'forgot_email' && (
            <Box component="form" onSubmit={handleSendForgotOtp} sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Enter your email address and we will send you an OTP to reset your password.
              </Typography>
              <TextField
                margin="normal"
                required
                fullWidth
                id="forgot-email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                placeholder="you@example.com"
                sx={AUTOFILL_SX}
                InputLabelProps={{ shrink: true }}
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2, py: 1.5 }}
                disabled={loading}
              >
                {loading ? 'Sending OTP...' : 'Send OTP'}
              </Button>
              <Box textAlign="center">
                <MuiLink component="button" type="button" variant="body2" onClick={() => { setView('login'); setError(null); setSuccess(null); }}>
                  Back to Sign In
                </MuiLink>
              </Box>
            </Box>
          )}

          {view === 'forgot_otp' && (
            <Box component="form" onSubmit={handleResetPassword} sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="forgot-otp"
                label="Enter OTP"
                placeholder="6-digit code"
                sx={AUTOFILL_SX}
                InputLabelProps={{ shrink: true }}
                value={forgotOtp}
                onChange={(e) => setForgotOtp(e.target.value)}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="new-password"
                label="New Password"
                type={showNewPassword ? 'text' : 'password'}
                placeholder="Choose a new password"
                sx={AUTOFILL_SX}
                InputLabelProps={{ shrink: true }}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <PasswordVisibilityToggle
                      visible={showNewPassword}
                      onToggle={() => setShowNewPassword((prev) => !prev)}
                    />
                  ),
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2, py: 1.5 }}
                disabled={loading}
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
              <Box textAlign="center">
                <MuiLink component="button" type="button" variant="body2" onClick={() => { setView('login'); setError(null); setSuccess(null); }}>
                  Back to Sign In
                </MuiLink>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
}

export default LoginPage;
